import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

import {
  Autocomplete,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { EditOutlined, MapsHomeWorkOutlined } from "@mui/icons-material";

import { BuildingProperties } from "api/onboardingTypes";
import { useToken } from "contexts/UserContext";
import { useErrorInfo, useSetError } from "contexts/ErrorContext";
import { fetchCitiesList } from "api/onboarding";
import { CustomCodes, ErrorResponse } from "contexts/ErrorContextData";
import { CityResponse } from "contexts/GreenEnergyContext";

const PROPERTY_MAP: Record<BuildingProperties["propertyType"], string> = {
  house: "Residência",
  school: "Escola",
  enterprise: "Empresa",
  condo: "Condomínio",
  other: "Outro",
};

export const BuildingDetail = forwardRef(
  ({ startValues }: BuildingDetailProps, ref) => {
    const setError = useSetError();
    const token = useToken();
    const errorInfo = useErrorInfo();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [name, setName] = useState<BuildingProperties["propertyName"]>(
      startValues?.propertyName || ""
    );
    const [cities, setCities] = useState<CityResponse>([]);
    const [chosenCity, setChosenCity] = useState<CityResponse[0] | null>(null);
    const [propertyType, setPropertyType] = useState<
      BuildingProperties["propertyType"] | null
    >(startValues?.propertyType || null);

    const formError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

    useImperativeHandle(ref, () => ({
      isFormValid: isFormValid,
      getFormValues: () => ({
        propertyName: name,
        city: chosenCity ? `${chosenCity.city} - ${chosenCity.sigla}` : "",
        propertyType: propertyType,
      }),
    }));

    useEffect(() => {
      const getCities = async () => {
        try {
          const data = await fetchCitiesList(token);
          setCities(data);
        } catch (error) {
          setError(error as ErrorResponse);
        } finally {
          setIsLoading(false);
        }
      };
      getCities();
    }, [token, setError]);

    const isFormValid = (): boolean =>
      (!name && !chosenCity && !propertyType) ||
      (!!name && !!chosenCity && !!propertyType);

    return (
      <>
        <Grid item container direction="row" alignItems="center">
          <MapsHomeWorkOutlined sx={{ mr: 1 }} />
          <Typography variant="h3" sx={{ mb: 0 }}>
            Informações Gerais
          </Typography>
        </Grid>
        <TextField
          label="Nome"
          value={name}
          error={formError && !name}
          onChange={({ target }) => {
            setName(target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EditOutlined />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          sx={{ flex: 1 }}
          clearOnBlur
          value={chosenCity}
          getOptionLabel={({ city, sigla }) => `${city} | ${sigla}`}
          onChange={(e, newValue) => setChosenCity(newValue)}
          disabled={!cities?.length}
          loading={isLoading}
          loadingText="Carregando a lista de cidades..."
          options={cities}
          renderInput={(params) => (
            <TextField
              label="Cidade"
              {...params}
              error={formError && !chosenCity}
              variant="outlined"
            />
          )}
        />
        <Autocomplete
          sx={{ flex: 1 }}
          clearOnBlur
          value={propertyType}
          getOptionLabel={(key) => PROPERTY_MAP[key]}
          // @ts-expect-error wont't be a random string
          onChange={(e, newValue) => setPropertyType(newValue)}
          options={Object.keys(PROPERTY_MAP)}
          renderInput={(params) => (
            <TextField
              label="Tipo"
              {...params}
              error={formError && !propertyType}
              variant="outlined"
            />
          )}
        />
      </>
    );
  }
);

export type BuildingDetailProps = {
  startValues?: Partial<BuildingProperties>;
};
